"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Image as GalleryImage } from "@/types";
import { createDemoGalleryImage, DEMO_PHOTOS, type DemoPhoto } from "./photowallDemoPhotos";

// Enough history for every card still in flight; older uploads are dropped.
const MAX_KEPT_UPLOADS = 24;
const FIRST_UPLOAD_ID = 1000;
export interface PhotowallDemoFeed {
  images: GalleryImage[];
  uploadCount: number;
  /** Simulates guests finishing uploads, spread unevenly over `spreadMs`. */
  addUploads: (count: number, spreadMs?: number) => void;
  cancelUploads: () => void;
}
const BASE_IMAGES = DEMO_PHOTOS.map(photo => createDemoGalleryImage(photo.id, photo));

/** Uploads land in uneven clusters, the way several phones finish at once. */
function getUploadDelays(count: number, spreadMs: number): number[] {
  if (count <= 1) return [0];
  return Array.from({
    length: count
  }, (_, index) => index === 0 ? 0 : spreadMs * Math.pow(Math.random(), 0.75)).sort((a, b) => a - b);
}
export function usePhotowallDemoFeed() {
  const $ = _c(10);
  const [images, setImages] = useState(BASE_IMAGES);
  const [uploadCount, setUploadCount] = useState(0);
  const nextIdRef = useRef(FIRST_UPLOAD_ID);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const photoBagRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const timersRef = useRef(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (photoBagRef.current.length === 0) {
        photoBagRef.current = [...DEMO_PHOTOS].sort(_temp);
      }
      return photoBagRef.current.shift() ?? DEMO_PHOTOS[0];
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const takePhoto = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      timersRef.current.forEach(_temp2);
      timersRef.current.clear();
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const cancelUploads = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (count, t5) => {
      const spreadMs = t5 === undefined ? 0 : t5;
      getUploadDelays(count, spreadMs).forEach(delay => {
        const timer_0 = setTimeout(() => {
          timersRef.current.delete(timer_0);
          nextIdRef.current = nextIdRef.current + 1;
          const upload = createDemoGalleryImage(nextIdRef.current, takePhoto());
          setImages(current => {
            const uploads = current.slice(BASE_IMAGES.length);
            return [...BASE_IMAGES, ...uploads.slice(-(MAX_KEPT_UPLOADS - 1)), upload];
          });
          setUploadCount(_temp3);
        }, delay);
        timersRef.current.add(timer_0);
      });
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const addUploads = t4;
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => cancelUploads;
    t6 = [cancelUploads];
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] !== images || $[8] !== uploadCount) {
    t7 = {
      images,
      uploadCount,
      addUploads,
      cancelUploads
    };
    $[7] = images;
    $[8] = uploadCount;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}
function _temp3(current_0) {
  return current_0 + 1;
}
function _temp2(timer) {
  return clearTimeout(timer);
}
function _temp() {
  return Math.random() - 0.5;
}